import { JsonFormat, JsonFormatError } from "@redotech/json/format";
import { Json } from "@redotech/json/json";
import { isDef } from "@redotech/util/checks";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod/v4";

const iso8601Regex =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;
const iso8601DateString = z
  .string()
  .refine((value) => iso8601Regex.test(value), {
    message: "Invalid ISO 8601 date format",
  });

const dateOrStringOrNumberAsIso8601DateString = z
  .union([z.date(), z.string(), z.number(), z.null(), z.undefined()])
  .transform((value) => {
    if (typeof value === "string") {
      return iso8601Regex.test(value) ? value : new Date(value).toISOString();
    } else if (value instanceof Date) {
      return value.toISOString();
    } else if (value == null) {
      return new Date().toISOString();
    } else {
      return new Date(value).toISOString();
    }
  })
  .refine((value) => iso8601Regex.test(value), {
    message: "Invalid ISO 8601 date format",
  });

const stringOrNumberAsString = z.union([
  z.string(),
  z.number().transform(String),
]);

const numberOrStringAsNumber = z
  .union([z.number(), z.string()])
  .transform((value) => (typeof value === "string" ? +value : value));

const urlSchema = z.string().url();

const MoneySetSchema_ = z.object({
  amount: stringOrNumberAsString,
  currency_code: z.string(),
});
export const MoneySetSchema: z.ZodType<MoneySet> = MoneySetSchema_;
export interface MoneySet extends z.infer<typeof MoneySetSchema_> {}

const OptionalMoneySetSchema = z
  .object({ shop_money: MoneySetSchema, presentment_money: MoneySetSchema })
  .nullish();

const OptionalMoneySetOrStringOrArrayAsOptionalMoneySet = z
  .union([z.string(), OptionalMoneySetSchema, z.array(z.any())])
  .transform((value) => {
    // CommentSold orders just seem to send whatever they want here-- usually a string with a "0" or an empty array.
    if (typeof value === "string") {
      return undefined;
    } else if (Array.isArray(value)) {
      return undefined;
    } else {
      return value;
    }
  });

const AmountTotalOrArrayAsString = z
  .union([z.string(), z.array(z.any())])
  .transform((value) => {
    if (typeof value === "string") {
      return value;
    } else {
      return "0";
    }
  });

const CustomerAddressSchema_ = z.object({
  address1: z.string().nullish(),
  address2: z.string().nullish(),
  city: z.string().nullish(),
  company: z.string().nullish(),
  country: z.string().nullish(),
  first_name: z.string().nullish(),
  last_name: z.string().nullish(),
  phone: z.string().nullish(),
  province: z.string().nullish(),
  zip: z.string().nullish(),
  name: z.string().nullish(),
  province_code: z.string().nullish(),
  country_code: z.string().nullish(),
  latitude: z.number().nullish(),
  longitude: z.number().nullish(),
});
const CustomerAddressSchema: z.ZodType<CustomerAddress> =
  CustomerAddressSchema_;
export interface CustomerAddress
  extends z.infer<typeof CustomerAddressSchema_> {}

const ClientDetailsSchema_ = z.object({
  accept_language: z.string().nullish(),
  browser_height: z.number().nullish(),
  browser_ip: z.string().nullish(),
  browser_width: z.number().nullish(),
  session_hash: z.string().nullish(),
  user_agent: z.string().nullish(),
});
const ClientDetailsSchema: z.ZodType<ClientDetails> = ClientDetailsSchema_;
export interface ClientDetails extends z.infer<typeof ClientDetailsSchema_> {}

const DiscountApplicationSchemaAllocationMethodEnum = createEnum(
  ["across", "each", "one"],
  { enumName: "DiscountApplicationAllocationMethod" },
);
const DiscountApplicationSchemaTargetSelectionEnum = createEnum(
  ["all", "entitled", "explicit"],
  { enumName: "DiscountApplicationTargetSelection" },
);
const DiscountApplicationSchemaTargetTypeEnum = createEnum(
  ["line_item", "shipping_line"],
  { enumName: "DiscountApplicationTargetType" },
);
const DiscountApplicationSchemaTypeEnum = createEnum(
  ["automatic", "manual", "script", "discount_code"],
  { enumName: "DiscountApplicationType" },
);
const DiscountApplicationValueTypeEnum = createEnum(
  ["fixed_amount", "percentage"],
  { enumName: "DiscountApplicationValueType" },
);

const DiscountApplicationSchema_ = z.object({
  allocation_method:
    DiscountApplicationSchemaAllocationMethodEnum.default("across"),
  code: z.string().nullish(),
  description: z.string().nullish(),
  target_selection:
    DiscountApplicationSchemaTargetSelectionEnum.default("explicit"),
  target_type: DiscountApplicationSchemaTargetTypeEnum.default("line_item"),
  title: z.string().nullish(),
  type: DiscountApplicationSchemaTypeEnum.default("manual"),
  value_type: DiscountApplicationValueTypeEnum.default("fixed_amount"),
  value: z.string(),
});
const DiscountApplicationSchema: z.ZodType<DiscountApplication> =
  DiscountApplicationSchema_;
export interface DiscountApplication
  extends z.infer<typeof DiscountApplicationSchema_> {}

const DiscountCodeValueTypeEnum = createEnum(
  ["fixed_amount", "percentage", "shipping"],
  { enumName: "DiscountCodeValueType" },
);
const DiscountCodeSchema_ = z.object({
  amount: z.string(),
  code: z.string(),
  type: DiscountCodeValueTypeEnum,
});
const DiscountCodeSchema: z.ZodType<DiscountCode> = DiscountCodeSchema_;
export interface DiscountCode extends z.infer<typeof DiscountCodeSchema_> {}

const TaxLineSchema_ = z.object({
  title: z.string().nullish(),
  price: z.string(),
  rate: z.number().nullish(),
  price_set: OptionalMoneySetSchema,
  channel_liable: z.boolean().nullish(),
});
export const TaxLineSchema: z.ZodType<TaxLine> = TaxLineSchema_;
export interface TaxLine extends z.infer<typeof TaxLineSchema_> {}

const DiscountAllocationSchema_ = z.object({
  amount: z.string(),
  discount_application_index: z.number(),
  amount_set: OptionalMoneySetSchema,
});
const DiscountAllocationSchema: z.ZodType<DiscountAllocation> =
  DiscountAllocationSchema_;
export interface DiscountAllocation
  extends z.infer<typeof DiscountAllocationSchema_> {}

const CompleteOrderFulfillmentStatusZodSchema = z.object({
  _value: z.string(),
  _dbValue: z.string().nullable(),
  _displayValue: z.string(),
});

/**
 * In OrderFulfillmentStatusEnum, unfulfilled is represented as null. This
 * CompleteOrderFulfillmentStatus enum is for convenience.
 */
export class CompleteOrderFulfillmentStatus {
  static readonly FULFILLED = new CompleteOrderFulfillmentStatus(
    "fulfilled",
    "fulfilled",
    "Fulfilled",
  );
  static readonly RESTOCKED = new CompleteOrderFulfillmentStatus(
    "restocked",
    "restocked",
    "Restocked",
  );
  static readonly PARTIAL = new CompleteOrderFulfillmentStatus(
    "partial",
    "partial",
    "Partial",
  );
  static readonly NOT_ELIGIBLE = new CompleteOrderFulfillmentStatus(
    "not_eligible",
    "not_eligible",
    "Not eligible",
  );
  static readonly UNFULFILLED = new CompleteOrderFulfillmentStatus(
    "unfulfilled",
    null,
    "Unfulfilled",
  );
  static readonly CANCELED = new CompleteOrderFulfillmentStatus(
    "canceled",
    "canceled",
    "Canceled",
  );

  static readonly allValues = [
    CompleteOrderFulfillmentStatus.FULFILLED.value,
    CompleteOrderFulfillmentStatus.RESTOCKED.value,
    CompleteOrderFulfillmentStatus.PARTIAL.value,
    CompleteOrderFulfillmentStatus.NOT_ELIGIBLE.value,
    CompleteOrderFulfillmentStatus.UNFULFILLED.value,
    CompleteOrderFulfillmentStatus.CANCELED.value,
  ];

  static readonly allDbValues = [
    CompleteOrderFulfillmentStatus.FULFILLED.dbValue,
    CompleteOrderFulfillmentStatus.RESTOCKED.dbValue,
    CompleteOrderFulfillmentStatus.PARTIAL.dbValue,
    CompleteOrderFulfillmentStatus.NOT_ELIGIBLE.dbValue,
    CompleteOrderFulfillmentStatus.UNFULFILLED.dbValue,
    CompleteOrderFulfillmentStatus.CANCELED.dbValue,
  ];

  static fromValue(value: string): CompleteOrderFulfillmentStatus {
    switch (value) {
      case CompleteOrderFulfillmentStatus.FULFILLED.value:
        return CompleteOrderFulfillmentStatus.FULFILLED;
      case CompleteOrderFulfillmentStatus.RESTOCKED.value:
        return CompleteOrderFulfillmentStatus.RESTOCKED;
      case CompleteOrderFulfillmentStatus.PARTIAL.value:
        return CompleteOrderFulfillmentStatus.PARTIAL;
      case CompleteOrderFulfillmentStatus.NOT_ELIGIBLE.value:
        return CompleteOrderFulfillmentStatus.NOT_ELIGIBLE;
      case CompleteOrderFulfillmentStatus.UNFULFILLED.value:
        return CompleteOrderFulfillmentStatus.UNFULFILLED;
      case CompleteOrderFulfillmentStatus.CANCELED.value:
        return CompleteOrderFulfillmentStatus.CANCELED;
      default:
        throw new Error(
          `Invalid CompleteOrderFulfillmentStatus value ${value}`,
        );
    }
  }

  static fromDbValue(
    dbValue: string | null | undefined,
  ): CompleteOrderFulfillmentStatus | undefined {
    switch (dbValue) {
      case CompleteOrderFulfillmentStatus.FULFILLED.dbValue:
        return CompleteOrderFulfillmentStatus.FULFILLED;
      case CompleteOrderFulfillmentStatus.RESTOCKED.dbValue:
        return CompleteOrderFulfillmentStatus.RESTOCKED;
      case CompleteOrderFulfillmentStatus.PARTIAL.dbValue:
        return CompleteOrderFulfillmentStatus.PARTIAL;
      case CompleteOrderFulfillmentStatus.NOT_ELIGIBLE.dbValue:
        return CompleteOrderFulfillmentStatus.NOT_ELIGIBLE;
      case CompleteOrderFulfillmentStatus.UNFULFILLED.dbValue:
        return CompleteOrderFulfillmentStatus.UNFULFILLED;
      case CompleteOrderFulfillmentStatus.CANCELED.dbValue:
        return CompleteOrderFulfillmentStatus.CANCELED;
      default:
        return undefined;
    }
  }

  static jsonFormat: JsonFormat<CompleteOrderFulfillmentStatus> = {
    read(json: Json): CompleteOrderFulfillmentStatus {
      if (
        typeof json === "string" &&
        CompleteOrderFulfillmentStatus.allValues.includes(json)
      ) {
        return CompleteOrderFulfillmentStatus.fromValue(json)!;
      } else {
        throw new JsonFormatError(
          `Invalid CompleteOrderFulfillmentStatus ${json}`,
          [],
        );
      }
    },
    write(orderFulfillmentStatus: CompleteOrderFulfillmentStatus): Json {
      return orderFulfillmentStatus.value;
    },
  };

  /**
   * Attempts to parse as an object first and then as a nullable string so the
   * RPC layer doesn't have to have a different schema than the db layer.
   */
  static zodParser = CompleteOrderFulfillmentStatusZodSchema.transform(
    (obj) => {
      return CompleteOrderFulfillmentStatus.fromValue(obj._value);
    },
  ).or(
    z
      .string()
      .nullable()
      .transform(
        (dbValue: string | null) =>
          CompleteOrderFulfillmentStatus.fromDbValue(dbValue)!,
      )
      .refine((value) => isDef(value), {
        message: "Unable to parse CompleteOrderFulfillmentStatus from string",
      }),
  );

  static fromShopifyOrder(
    shopifyOrder: Pick<ShopifyOrder, "cancelled_at" | "fulfillment_status">,
  ): CompleteOrderFulfillmentStatus | undefined {
    if (shopifyOrder.cancelled_at) {
      return CompleteOrderFulfillmentStatus.CANCELED;
    }

    switch (shopifyOrder.fulfillment_status) {
      case null:
        return CompleteOrderFulfillmentStatus.UNFULFILLED;
      case "fulfilled":
        return CompleteOrderFulfillmentStatus.FULFILLED;
      case "restocked":
        return CompleteOrderFulfillmentStatus.RESTOCKED;
      case "partial":
        return CompleteOrderFulfillmentStatus.PARTIAL;
      case "not_eligible":
        return CompleteOrderFulfillmentStatus.NOT_ELIGIBLE;
      default:
        return undefined;
    }
  }

  private constructor(
    private readonly _value: string,
    private readonly _dbValue: string | null,
    private readonly _displayValue: string,
  ) {}

  get value(): string {
    return this._value;
  }

  get dbValue(): string | null {
    return this._dbValue;
  }

  get displayValue(): string {
    return this._displayValue;
  }
}

export const OrderFulfillmentStatusEnum = createEnum(
  ["fulfilled", "unfulfilled", "restocked", "partial", "not_eligible"],
  { enumName: "OrderFulfillmentStatus" },
).describe(`- fulfilled: All items in the order have been shipped
- unfulfilled: No items in the order have been shipped
- restocked: Order has been cancelled and all items within that order have been returned to inventory
- partial: Some items in the order have been shipped, but not all
- not_eligible: Items have been fulfilled and do not require shipping
`);
// NOTE: The above describe is used by LLMs to understand the available order fulfillment statuses, please update this if you change the enum and
// use clear natural language to describe the statuses.

export type OrderFulfillmentStatus = z.infer<typeof OrderFulfillmentStatusEnum>;

const PropertySchema = z.object({
  name: z.string().nullish(),
  value: z.any().nullish(),
});

export const LineItemSchema_ = z.object({
  id: z.union([z.number(), z.string()]),
  current_quantity: z.number().nullish(),
  tip_payment_gateway: z.string().nullish(),
  tip_payment_method: z.string().nullish(),
  image: z
    .object({
      id: z.number(),
      product_id: z.union([z.number(), z.string()]),
      position: z.number(),
      created_at: dateOrStringOrNumberAsIso8601DateString,
      updated_at: dateOrStringOrNumberAsIso8601DateString,
      alt: z.string().nullish(),
      width: z.number().nullish(),
      height: z.number().nullish(),
      src: z.string().nullish().default(""),
      variant_ids: z.array(z.number()),
      admin_graphql_api_id: z.string().nullish(),
    })
    .nullish(),
  /**
   * This will be true if the item is a redo returns coverage item and it was
   * purchased from a valid location, or if the orders are covered by the merchant.
   */
  protected: z.union([z.boolean(), z.array(z.string())]).nullish(), // sometimes this is a boolean, others it is an array like ["re:do"] idk why
  /**
   * This will be true if the item is a redo final sale returns coverage item and
   * it was purchased from a valid location.
   *
   * @deprecated For use of determining if the item is covered by final sale. Use Order.finalSaleLineItemsProtection instead.
   */
  finalSaleProtected: z.boolean().nullish(),
  tags: z.array(z.string()).nullish(),
  tip: z
    .object({
      payment_method: z.string().nullish(),
      payment_gateway: z.string().nullish(),
    })
    .nullish(),
  attributed_staffs: z
    .array(z.object({ id: z.string(), quantity: z.number() }))
    .nullish(),
  admin_graphql_api_id: z.string().nullish(),
  variant_id: z.union([z.number(), z.string()]).nullish(),
  title: z.string().default(""),
  quantity: z.number().default(1),
  price: z.string().default("0"),
  price_set: OptionalMoneySetSchema,
  pre_tax_price: z.string().nullish(),
  pre_tax_price_set: OptionalMoneySetSchema,
  grams: z.number().nullish(),
  sku: z.string().nullish(),
  vendor: z.string().nullish(),
  variant_title: z.string().nullish(),
  fulfillment_service: z.string().nullish(),
  product_id: z.union([z.number(), z.string()]).nullish(),
  additional_product_id: z.string().nullish(),
  requires_shipping: z.boolean().nullish(),
  taxable: z.boolean().nullish(),
  gift_card: z.boolean().nullish(),
  name: z.string().default(""),
  variant_inventory_management: z.string().nullish(),
  properties: z.array(PropertySchema).default([]),
  product_exists: z.boolean().nullish(),
  fulfillable_quantity: z.number().default(1),
  returnable_quantity: z.number().nullish(),
  total_discount: stringOrNumberAsString.default("0"),
  fulfillment_status: OrderFulfillmentStatusEnum.nullish(),
  total_discount_set: OptionalMoneySetSchema,
  destination_location: z
    .object({
      id: z.number(),
      country_code: z.string().nullish(),
      province_code: z.string().nullish(),
      name: z.string().nullish(),
      address1: z.string().nullish(),
      address2: z.string().nullish(),
      city: z.string().nullish(),
      zip: z.string().nullish(),
    })
    .nullish(),
  origin_location: z
    .object({
      id: z.number(),
      country_code: z.string(),
      province_code: z.string(),
      name: z.string(),
      address1: z.string(),
      address2: z.string().nullish(),
      city: z.string(),
      zip: z.string(),
    })
    .nullish(),
  discount_allocations: z.array(DiscountAllocationSchema).default([]),
  fulfillment_line_item_id: z.string().nullish(),
  tax_code: z.string().nullish(),
  tax_lines: z.array(TaxLineSchema).default([]),
  duties: z
    .array(
      z.object({
        id: z.number(),
        harmonized_system_code: z.string().nullish(),
        country_code_of_origin: z.string().nullish(),
        price_set: OptionalMoneySetSchema,
        tax_lines: z.array(TaxLineSchema),
        admin_graphql_api_id: z.string(),
      }),
    )
    .default([]),
  discount_price: z.string().nullish().optional(),
  reason: z.string().nullish().optional(),
  loyaltyAmount: numberOrStringAsNumber.nullish(),
  delivery_date: z.string().nullish(),
  duties_included: z.any().optional(),
  merchant_business_entity_id: z.any().optional(),
  total_cash_rounding_payment_adjustment_set: z.any().optional(),
  total_cash_rounding_refund_adjustment_set: z.any().optional(),
  redoEligible: z.boolean().nullish(), // Not from Shopify, added by redo
  redoPackageProtectionEligible: z.boolean().nullish(), // Not from Shopify, added by redo
  productMetadata: z.any().optional(), // Not from Shopify, added by redo. But specifically for Commerce Cloud GlobalE Orders.
  orderTimeCollections: z.array(z.string()).nullish(), // Not from Shopify, added by redo for BigCommerce
  redoProduct: z.boolean().nullish(), // Not from Shopify, added by redo to mark Redo coverage items
});
export const LineItemSchema: z.ZodType<LineItem> = LineItemSchema_;
export interface LineItem extends z.infer<typeof LineItemSchema_> {}

const ShipmentStatusEnum = createEnum(
  [
    "label_printed",
    "label_purchased",
    "attempted_delivery",
    "ready_for_pickup",
    "confirmed",
    "in_transit",
    "out_for_delivery",
    "delivered",
    "failure",
    "picked_up",
    "stalled_in_transit",
    "delayed",
  ],
  { enumName: "ShipmentStatus" },
);

const FulfillmentStatusEnum = createEnum(
  ["pending", "open", "success", "cancelled", "error", "failure"],
  { enumName: "FulfillmentStatus" },
);

const OriginAddressSchema_ = z.object({
  address1: z.string().nullish(),
  address2: z.string().nullish(),
  city: z.string().nullish(),
  country_code: z.string().nullish(),
  province_code: z.string().nullish(),
  zip: z.string().nullish(),
});
export const OriginAddressSchema: z.ZodType<OriginAddress> =
  OriginAddressSchema_;
export interface OriginAddress extends z.infer<typeof OriginAddressSchema_> {}

const FulfillmentStatusWithFulfilledFallback = z
  .union([
    FulfillmentStatusEnum,
    z.literal("Fulfilled"),
    z.literal("fulfilled"),
    z.undefined(),
  ])
  .transform((value) => {
    // Need to use both strings to make types narrow correctly.
    // Fallback option mirrors commentsold-order-sync.ts
    if (value === "fulfilled" || value === "Fulfilled" || value === undefined) {
      return "success" as const;
    }
    return value;
  });

const ShipmentStatusWithFulfilledFallback = z
  .union([
    ShipmentStatusEnum,
    z.literal("Fulfilled"),
    z.literal("fulfilled"),
    z.undefined(),
  ])
  .transform((value) => {
    // Need to use both strings to make types narrow correctly.
    // Fallback option mirrors commentsold-order-sync.ts
    if (value === "fulfilled" || value === "Fulfilled" || value === undefined) {
      return "in_transit" as const;
    }
    return value;
  });

const FulfillmentSchema_ = z.object({
  admin_graphql_api_id: z.string().nullish(),
  created_at: iso8601DateString.default(new Date().toISOString()),
  id: z.union([z.number(), z.string()]).nullish(), // null for CS orders
  line_items: z.array(LineItemSchema),
  location_id: z.number().nullish(),
  location: z.object({ name: z.string().nullish() }).nullish(),
  name: z.string().nullish(),
  order_id: z.number().nullish(),
  origin_address: OriginAddressSchema.nullish(),
  receipt: z.any().optional(),
  status: FulfillmentStatusWithFulfilledFallback,
  service: z.string().nullish(),
  shipment_status: ShipmentStatusWithFulfilledFallback.nullish(),
  tracking_company: z.string().nullish(),
  tracking_number: z.string().nullish(),
  tracking_numbers: z.array(z.string()).nullish(),
  tracking_url: z.string().nullish(),
  tracking_urls: z.union([z.array(z.string()).nullish(), z.null()]), // null for CS orders
  updated_at: iso8601DateString.nullish(),
  variant_inventory_management: z.string().nullish(),
});
const FulfillmentSchema: z.ZodType<Fulfillment> = FulfillmentSchema_;
export interface Fulfillment extends z.infer<typeof FulfillmentSchema_> {}

const MarketingConsentSchema_ = z.object({
  state: z.string(),
  opt_in_level: z.string().nullish(),
  consent_updated_at: iso8601DateString.nullish(),
  consent_collected_from: z.string().nullish(),
});
const MarketingConsentSchema: z.ZodType<MarketingConsent> =
  MarketingConsentSchema_;
export interface MarketingConsent
  extends z.infer<typeof MarketingConsentSchema_> {}

const CustomerStateSchema = createEnum(
  ["disabled", "invited", "enabled", "declined"],
  { enumName: "CustomerState" },
);

const CustomerSchema_ = z.object({
  accepts_marketing: z.boolean().nullish(),
  accepts_marketing_updated_at: iso8601DateString.nullish(),
  addresses: z.array(CustomerAddressSchema).nullish(),
  admin_graphql_api_id: z.string().nullish(),
  created_at: iso8601DateString.nullish(),
  currency: z.string().nullish(),
  default_address: CustomerAddressSchema_.merge(
    z.object({
      default: z.boolean(),
      id: z.number(),
      country_name: z.string().nullish(),
      customer_id: z.number(),
    }),
  ).nullish(),
  email: z.string().nullish(),
  email_marketing_consent: MarketingConsentSchema.nullish(),
  id: z.union([z.number(), z.string()]).nullish(), // always provided by Shopify, optional in Redo API
  first_name: z.string().nullish(),
  last_name: z.string().nullish(),
  last_order_id: z.number().nullish(),
  last_order_name: z.string().nullish(),
  metafield: z
    .object({
      key: z.string(),
      value: z.string(),
      value_type: z.string(),
      namespace: z.string(),
    })
    .nullish(),
  marketing_opt_in_level: z.string().nullish(), // deprecated, use email_marketing_consent
  multipass_identifier: z.string().nullish(),
  note: z.string().nullish(),
  orders_count: z.number().nullish(),
  password: z.string().nullish(),
  password_confirmation: z.string().nullish(),
  phone: z.string().nullish(),
  sms_marketing_consent: MarketingConsentSchema.nullish(),
  state: CustomerStateSchema.nullish(), // always provided by Shopify, optional in Redo API
  tags: z.string().nullish(),
  tax_exempt: z.boolean().nullish(),
  tax_exemptions: z.array(z.string()).nullish(),
  total_spent: z.string().nullish(),
  updated_at: iso8601DateString.nullish(),
  verified_email: z.boolean().nullish(), // always provided by Shopify, optional in Redo API
});
const CustomerSchema: z.ZodType<Customer> = CustomerSchema_;
export interface Customer extends z.infer<typeof CustomerSchema_> {}

const PaymentTermsSchema_ = z.object({
  id: z.number(),
  amount: z.number().nullish(),
  currency: z.string().nullish(),
  payment_terms_name: z.string(),
  payment_terms_type: z.string(),
  created_at: iso8601DateString,
  updated_at: iso8601DateString,
  due_in_days: z.number().nullish(),
  payment_schedules: z.array(
    z.object({
      id: z.number(),
      amount: z.string(),
      currency: z.string(),
      issued_at: iso8601DateString.nullish(),
      due_at: iso8601DateString.nullish(),
      completed_at: iso8601DateString.nullish(),
      expected_payment_method: z.string().nullish(),
      created_at: iso8601DateString,
      updated_at: iso8601DateString,
    }),
  ),
});
const PaymentTermsSchema: z.ZodType<PaymentTerms> = PaymentTermsSchema_;
export interface PaymentTerms extends z.infer<typeof PaymentTermsSchema_> {}

const ShippingLineSchema_ = z.object({
  id: z.union([z.number(), z.string()]),
  delivery_category: z.string().nullish(),
  phone: z.string().nullish(),
  discount_allocations: z.array(
    z.object({
      amount: z.string(),
      discount_application_index: z.number(),
      amount_set: z.object({
        shop_money: MoneySetSchema,
        presentment_money: MoneySetSchema,
      }),
    }),
  ),
  code: z.string().nullish(),
  discounted_price: z.string(),
  discounted_price_set: z.object({
    shop_money: MoneySetSchema,
    presentment_money: MoneySetSchema,
  }),
  price: z.string(),
  price_set: z.object({
    shop_money: MoneySetSchema,
    presentment_money: MoneySetSchema,
  }),
  source: z.string().nullish(),
  title: z.string(),
  tax_lines: z.array(TaxLineSchema),
  carrier_identifier: z.string().nullish(),
  requested_fulfillment_service_id: z.string().nullish(),
  is_removed: z.boolean().nullish(),
});
export const ShippingLineSchema: z.ZodType<ShippingLine> = ShippingLineSchema_;
export interface ShippingLine extends z.infer<typeof ShippingLineSchema_> {}

const RefundOrderAdjustmentKindEnum = createEnum(
  ["shipping_refund", "refund_discrepancy"],
  { enumName: "RefundOrderAdjustmentKind" },
);
const RefundDutyTypeEnum = createEnum(["FULL", "PROPORTIONAL"], {
  enumName: "RefundDutyType",
});
const RefundLineItemRestockTypeEnum = createEnum(
  ["no_restock", "cancel", "return", "legacy_restock"],
  { enumName: "RefundLineItemRestockType" },
);
const TransactionErrorCodeEnum = createEnum(
  [
    "incorrect_number",
    "invalid_number",
    "invalid_expiry_date",
    "invalid_cvc",
    "expired_card",
    "incorrect_cvc",
    "incorrect_zip",
    "incorrect_address",
    "card_declined",
    "processing_error",
    "call_issuer",
    "pick_up_card",
    "generic_error",
  ],
  { enumName: "TransactionErrorCode" },
);
const TransactionKindEnum = createEnum(
  ["authorization", "capture", "sale", "void", "refund"],
  { enumName: "TransactionKind" },
);
const TransactionStatusEnum = createEnum(
  ["pending", "error", "success", "failure", "deferred"],
  { enumName: "TransactionStatus" },
);

const PaymentDetailsSchema_ = z.object({
  credit_card_bin: z.string().nullish(),
  avs_result_code: z.string().nullish(),
  cvv_result_code: z.string().nullish(),
  credit_card_number: z.string(),
  credit_card_company: z.string(),
  credit_card_name: z.string().nullish(),
  credit_card_wallet: z.string().nullish(),
  credit_card_expiration_month: z.number().nullish(),
  credit_card_expiration_year: z.number().nullish(),
  buyer_action_info: z.object({}).nullish(),
  payment_method_name: z.string().nullish(),
});
const PaymentDetailsSchema: z.ZodType<PaymentDetails> = PaymentDetailsSchema_;
export interface PaymentDetails extends z.infer<typeof PaymentDetailsSchema_> {}

const TransactionSchema_ = z.object({
  admin_graphql_api_id: z.string(),
  amount: z.string(),
  authorization: z.string().nullish(),
  authorization_expires_at: iso8601DateString.nullish(),
  created_at: iso8601DateString,
  currency: z.string().nullish(),
  device_id: z.number().nullish(),
  error_code: TransactionErrorCodeEnum.nullish(),
  extended_authorization_attributes: z
    .object({
      standard_authorization_expires_at: iso8601DateString,
      extended_authorization_expires_at: iso8601DateString.nullish(),
    })
    .nullish(),
  gateway: z.string().nullish(),
  id: z.number(),
  kind: TransactionKindEnum,
  location_id: z.number().nullish(),
  message: z.string().nullish(),
  order_id: z.number(),
  payment_details: PaymentDetailsSchema.nullish(),
  parent_id: z.number().nullish(),
  payment_id: z.string().nullish(),
  payments_refund_attributes: z
    .object({
      status: TransactionStatusEnum,
      acquirer_reference_number: z.string().nullish(),
    })
    .nullish(),
  processed_at: iso8601DateString,
  receipt: z.any(),
  source_name: z.string(),
  status: TransactionStatusEnum,
  total_unsettled_set: OptionalMoneySetSchema,
  test: z.boolean(),
  user_id: z.number().nullish(),
  currency_exchange_adjustment: z
    .object({
      id: z.number(),
      adjustment: z.string(),
      original_amount: z.string(),
      final_amount: z.string(),
      currency: z.string(),
    })
    .nullish(),
});
const TransactionSchema: z.ZodType<Transaction> = TransactionSchema_;
export interface Transaction extends z.infer<typeof TransactionSchema_> {}

const RefundSchema = z.object({
  admin_graphql_api_id: z.string(),
  order_id: z.number(),
  total_duties_set: OptionalMoneySetSchema,
  created_at: iso8601DateString,
  duties: z.array(
    z.object({
      duty_id: z.number(),
      amount_set: z.object({
        shop_money: MoneySetSchema,
        presentment_money: MoneySetSchema,
      }),
    }),
  ),
  id: z.number(),
  note: z.string().nullish(),
  order_adjustments: z.array(
    z.object({
      id: z.number(),
      order_id: z.number(),
      refund_id: z.number(),
      amount: z.string(),
      tax_amount: z.string(),
      reason: z.string(),
      kind: RefundOrderAdjustmentKindEnum,
      amount_set: z.object({
        shop_money: MoneySetSchema,
        presentment_money: MoneySetSchema,
      }),
      tax_amount_set: z.object({
        shop_money: MoneySetSchema,
        presentment_money: MoneySetSchema,
      }),
    }),
  ),
  processed_at: iso8601DateString,
  refund_duties: z
    .array(z.object({ duty_id: z.number(), refund_type: RefundDutyTypeEnum }))
    .nullish(),
  refund_line_items: z.array(
    z.object({
      id: z.number(),
      line_item: LineItemSchema,
      line_item_id: z.number(),
      quantity: z.number(),
      restock_type: RefundLineItemRestockTypeEnum.nullish(),
      location_id: z.number().nullish(),
      subtotal: z.number(),
      total_tax: z.number(),
      subtotal_set: z.object({
        shop_money: MoneySetSchema,
        presentment_money: MoneySetSchema,
      }),
      total_tax_set: z.object({
        shop_money: MoneySetSchema,
        presentment_money: MoneySetSchema,
      }),
    }),
  ),
  restock: z.boolean(),
  transactions: z.array(TransactionSchema),
  user_id: z.number().nullish(),
});

const SparseShopifyRefund = z.object({
  refund_line_items: z.array(
    z.object({ line_item_id: z.number(), quantity: z.number() }),
  ),
});

const AppSchema = z.object({
  id: z.string().nullish(),
  name: z.string().nullish(),
});
const PublicationSchema = z.object({
  id: z.string().nullish(),
  name: z.string().nullish(),
});

const CancelledReasonEnum = createEnum(
  ["customer", "fraud", "inventory", "declined", "other", "staff"],
  { enumName: "CancelledReason" },
);
export const FinancialStatusEnum = createEnum(
  [
    "authorized",
    "pending",
    "paid",
    "partially_paid",
    "partially_refunded",
    "refunded",
    "voided",
  ],
  { enumName: "FinancialStatus" },
);

export const orderIdSchema_ = z.union([z.number(), z.string()]);
export const orderIdSchema: z.ZodType<OrderId> = orderIdSchema_;

/** OrderId is unique per-team AND provider (meaning not necessarily unique per team _across_ providers, so a Shopify and a Commentsold order could have the same OrderId on a given team). See ProviderPrefixedOrderId for a unique order ID per-team ACROSS providers. */
export type OrderId = z.infer<typeof orderIdSchema_>;

/**
 * Schema for Shopify-shaped order data. This schema is used for both real Shopify orders
 * and synthetic orders from other platforms (CommentSold, SFOM, etc.) that are reshaped
 * to match this structure. Some fields marked optional/defaulted here are required by
 * Shopify's API but may be missing from other providers.
 */
export const ShopifyOrderSchema_ = z.object({
  admin_graphql_api_id: z.string().nullish(),
  app_id: z.number().nullish(),
  billing_address: CustomerAddressSchema.nullish(),
  browser_ip: z.string().nullish(),
  buyer_accepts_marketing: z.boolean().nullish(),
  cancel_reason: CancelledReasonEnum.nullish(),
  cancelled_at: iso8601DateString.nullish(),
  cart_token: z.string().nullish(),
  checkout_id: z.number().nullish(),
  checkout_token: z.string().nullish(),
  client_details: ClientDetailsSchema.nullish(),
  closed_at: iso8601DateString.nullish(),
  company: z
    .object({ id: z.number(), location_id: z.number().nullish() })
    .nullish(),
  confirmation_number: z.string().nullish(),
  confirmed: z.boolean().nullish(),
  contact_email: z.string().nullish(),
  created_at: dateOrStringOrNumberAsIso8601DateString,
  currency: z.string(),
  current_total_additional_fees_set:
    OptionalMoneySetOrStringOrArrayAsOptionalMoneySet.nullish(),
  current_total_discounts: AmountTotalOrArrayAsString.default("0"), // always provided by Shopify, defaults to "0" in Redo API
  current_total_discounts_set:
    OptionalMoneySetOrStringOrArrayAsOptionalMoneySet.nullish(),
  current_total_duties_set:
    OptionalMoneySetOrStringOrArrayAsOptionalMoneySet.nullish(),
  current_total_price: z.string().nullish(), // always provided by Shopify, optional in Redo API
  current_total_price_set: OptionalMoneySetSchema,
  current_subtotal_price: z.string().nullish(), // always provided by Shopify, optional in Redo API
  current_subtotal_price_set: OptionalMoneySetSchema,
  current_total_tax: z.string().nullish(), // always provided by Shopify, optional in Redo API
  current_total_tax_set: OptionalMoneySetSchema,
  customer: CustomerSchema.nullish(),
  customer_locale: z.string().nullish(),
  device_id: z.number().nullish(),
  discount_applications: z.array(DiscountApplicationSchema).default([]), // always provided by Shopify, defaults to [] in Redo API
  discount_codes: z.array(DiscountCodeSchema),
  email: z.string().nullish(),
  estimated_taxes: z.boolean().nullish(),
  financial_status: FinancialStatusEnum.nullish(),
  fulfillments: z.array(FulfillmentSchema),
  fulfillment_status: OrderFulfillmentStatusEnum.nullish(),
  gateway: z.string().nullish(),
  id: orderIdSchema,
  landing_site: z.string().nullish(),
  landing_site_ref: z.string().nullish(),
  line_items: z.array(LineItemSchema),
  location_id: z.number().nullish(),
  merchant_of_record_app_id: z.number().nullish(),
  name: z.string(),
  note: z.string().nullish(),
  note_attributes: z.array(PropertySchema).default([]), // always provided by Shopify, defaults to [] in Redo API
  number: z.union([z.number(), z.string()]).nullish(), // always provided by Shopify, optional in Redo API
  order_number: z.union([z.number(), z.string()]).nullish(),
  original_total_additional_fees_set: OptionalMoneySetSchema,
  original_total_duties_set: OptionalMoneySetSchema,
  payment_details: PaymentDetailsSchema.nullish(),
  payment_terms: PaymentTermsSchema.nullish(),
  payment_gateway_names: z.array(z.string()).nullish(),
  phone: z.string().nullish(),
  po_number: z.string().nullish(),
  presentment_currency: z.string().nullish(),
  processing_method: z.string().nullish(),
  processed_at: dateOrStringOrNumberAsIso8601DateString,
  reference: z.string().nullish(),
  referring_site: z.string().nullish(),
  refunds: z
    .union([z.array(RefundSchema), z.array(SparseShopifyRefund)])
    .default([]), // always provided by Shopify, defaults to [] in Redo API
  shipping_address: CustomerAddressSchema.nullish(),
  shipping_lines: z.array(ShippingLineSchema),
  source_identifier: z.string().nullish(),
  source_name: z.string().nullish(),
  source_url: z.string().nullish(),
  subtotal_price: z.string(),
  subtotal_price_set: z.object({
    shop_money: MoneySetSchema,
    presentment_money: MoneySetSchema,
  }),
  tags: z.string().nullish(),
  tax_exempt: z.boolean().nullish(),
  tax_lines: z.array(TaxLineSchema).default([]), // always provided by Shopify, defaults to [] in Redo API
  taxes_included: z.boolean().default(false), // always provided by Shopify, defaults to false in Redo API
  test: z.boolean().default(false), // always provided by Shopify, defaults to false in Redo API
  token: z.string().nullish(),
  total_discounts: z.string().nullish(),
  total_discounts_set: OptionalMoneySetSchema,
  total_line_items_price: stringOrNumberAsString.nullish(), // always provided by Shopify, optional in Redo API
  total_line_items_price_set: z.object({
    shop_money: MoneySetSchema,
    presentment_money: MoneySetSchema,
  }),
  total_outstanding: z.string().nullish(),
  total_price: z.string(),
  total_price_usd: z.string().nullish(),
  total_price_set: z.object({
    shop_money: MoneySetSchema,
    presentment_money: MoneySetSchema,
  }),
  total_shipping_price_set: OptionalMoneySetSchema,
  total_tax: z.string(),
  total_tax_set: z.object({
    shop_money: MoneySetSchema,
    presentment_money: MoneySetSchema,
  }),
  total_tip_received: z.string().nullish(),
  total_weight: z.number().nullish(),
  updated_at: dateOrStringOrNumberAsIso8601DateString,
  user_id: z.number().nullish(),
  order_status_url: urlSchema.nullish(),
  original_order: z.any().nullish(),
  app: AppSchema.nullish(),
  publication: PublicationSchema.nullish(),
});
export const ShopifyOrderSchema: z.ZodType<ShopifyOrder> = ShopifyOrderSchema_;
export interface ShopifyOrder extends z.infer<typeof ShopifyOrderSchema_> {}
